import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { LockedOverlay, LockedCard } from "@/components/LockedOverlay";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QuestCard } from "@/components/QuestCard";
import { MoodTracker, FocusTimer, ThoughtReframeTool, EnergyCheckIn } from "@/components/WellnessTools";
import { MeditationSection } from "@/components/MeditationSection";
import { SoundscapesSection } from "@/components/SoundscapesSection";
import axios from "axios";
import { 
  Sparkles, 
  ChevronRight,
  BookOpen,
  Archive,
  Compass,
  Wind,
  Brain,
  ShoppingBag,
  ExternalLink,
  Flame,
  Target,
  ArrowRight,
  ArrowLeft,
  Star,
  Clock,
  Zap,
  Bell,
  Lock,
  Crown
} from "lucide-react";

const TRUENORTH_STORE_URL = "https://www.truenorth-zone.com/";

// Theme images
const THEME_IMAGES = {
  cosmicSunflower: "https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png",
  compass: "https://images.unsplash.com/photo-1551090052-4684909d9ea9?w=800&q=80",
  compassNotebook: "https://images.pexels.com/photos/14436288/pexels-photo-14436288.jpeg?auto=compress&cs=tinysrgb&w=800",
  vintageMap: "https://images.unsplash.com/photo-1612257998095-1feb6bd2bcf9?w=800&q=80",
};

// Tab order: For You | Audio Vault | Digital Vault
const CATEGORIES = [
  { id: "for-you", label: "For You", icon: Star },
  { id: "wellness", label: "Audio Vault", icon: Brain },
  { id: "tools", label: "Digital Vault", icon: Zap },
];

// Tools - The Vault only (removed Daily Compass)
const TOOLS = [
  {
    id: "vault",
    title: "The Vault",
    description: "Worksheets, guides, and exercises",
    icon: Archive,
    gradient: "from-[#d0e0e8] to-[#b8d0d8]",
    iconColor: "text-[#507080]",
    route: "/vault"
  }
];

export default function DiscoverPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { subscription } = useSubscription();
  
  // Read initial tab from URL if present
  // Support both internal ids and user-friendly names
  const tabFromUrl = searchParams.get('tab');
  const scrollTarget = searchParams.get('scroll');
  const tabMapping = {
    'for-you': 'for-you',
    'audio_vault': 'wellness',
    'wellness': 'wellness',
    'digital_vault': 'tools',
    'tools': 'tools'
  };
  const initialTab = tabMapping[tabFromUrl] || 'for-you';
  
  const [activeCategory, setActiveCategory] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  
  // Handle scroll to specific section (e.g., quests)
  useEffect(() => {
    if (scrollTarget === 'quests' && activeCategory === 'tools') {
      // Wait for content to render, then scroll to quests section
      const timer = setTimeout(() => {
        const questsSection = document.getElementById('transformation-quests-section');
        if (questsSection) {
          questsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scrollTarget, activeCategory]);

  // Subscription access checks - pass user for admin bypass
  const canAccessTools = hasAccess(subscription.type, 'monthly', user);
  // Free users can access basic wellness features (check-in, journal, limited soundscapes/meditations)
  // The individual components handle gating for premium items
  const isFreeUser = subscription.type === 'none' && user?.role !== 'admin';

  useEffect(() => {
    // Just set loading to false since we removed quests fetch
    setLoading(false);
  }, []);

  // Accordion state for wellness tools in For You tab
  const [expandedTool, setExpandedTool] = useState(null);

  const handleToggle = (toolId) => {
    setExpandedTool(expandedTool === toolId ? null : toolId);
  };

  const renderForYou = () => (
    <div className="space-y-6">
      {/* START HERE Section - Wellness Tools in new order */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-5 h-5 text-amber-500" />
          <h2 className="font-heading font-bold text-lg" style={{ color: '#a69373' }}>Start Here</h2>
        </div>
        <div className="space-y-3">
          {/* 1. Wellness Check - MOVED TO FIRST */}
          <Card 
            className="glass-card overflow-hidden cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate("/wellness-check")}
            data-testid="foryou-wellness-check"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#d0dce8] to-[#c8d0e0] flex items-center justify-center">
                  <Brain className="w-6 h-6 text-[#5a7090]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold" style={{ color: '#a69373' }}>Wellness Check</h3>
                  <p className="text-foreground/60 text-sm">Assess your stress levels</p>
                </div>
                <ChevronRight className="w-5 h-5 text-foreground/30" />
              </div>
            </CardContent>
          </Card>
          {/* 2. Mood Tracker */}
          <MoodTracker 
            isExpanded={expandedTool === 'mood'} 
            onToggle={() => handleToggle('mood')} 
          />
          {/* 3. Energy Check-In */}
          <EnergyCheckIn 
            isExpanded={expandedTool === 'energy'} 
            onToggle={() => handleToggle('energy')} 
          />
          {/* 4. Breathing Exercise */}
          <Card 
            className="glass-card overflow-hidden cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate("/breathe")}
            data-testid="foryou-breathing"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#d4e8d8] to-[#c8e0d0] flex items-center justify-center">
                  <Wind className="w-6 h-6 text-[#5a8a60]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold" style={{ color: '#a69373' }}>Breathing Exercise</h3>
                  <p className="text-foreground/50 text-sm">Calming breath patterns</p>
                </div>
                <ChevronRight className="w-5 h-5 text-foreground/30" />
              </div>
            </CardContent>
          </Card>
          {/* 5. Thought Reframe Tool */}
          <ThoughtReframeTool 
            isExpanded={expandedTool === 'reframe'} 
            onToggle={() => handleToggle('reframe')} 
          />
          {/* 6. Focus Timer */}
          <FocusTimer 
            isExpanded={expandedTool === 'timer'} 
            onToggle={() => handleToggle('timer')} 
          />
        </div>
      </section>

      {/* RECOMMENDED NEXT Section - Shop Card */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-5 h-5 text-emerald-500" />
          <h2 className="font-heading text-[#a69373] text-lg">Recommended Next</h2>
        </div>
        <Card 
          className="glass-card overflow-hidden cursor-pointer group"
          onClick={() => window.open(TRUENORTH_STORE_URL, "_blank")}
          data-testid="shop-cta"
        >
          <CardContent className="p-0">
            <div className="relative h-32 overflow-hidden">
              <img 
                src={THEME_IMAGES.cosmicSunflower}
                alt="TrueNorth Store"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-heading text-lg mb-1">TrueNorth Store</h3>
                  <p className="text-white/70 text-sm">Journals, tools & resources</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );

  const renderTools = () => {
    // Lock Tools for free users
    if (!canAccessTools) {
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <Card className="glass-card max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-6">
                <Archive className="w-10 h-10 text-[#a69373]" />
              </div>
              <h2 className="text-xl font-heading text-white mb-3">Upgrade to access all tools</h2>
              <p className="text-white/60 text-sm mb-6">
                Unlock downloadable worksheets, guides, and exercises to support your transformation journey.
              </p>
              <a
                href={MEMBERSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold transition-colors"
                data-testid="tools-upgrade-btn"
              >
                <Crown className="w-5 h-5" />
                Upgrade Now
                <ExternalLink className="w-4 h-4" />
              </a>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Direct Vault contents - no intermediate click needed
    const VAULT_SECTIONS = [
      {
        id: "reflection",
        title: "REFLECTION",
        description: "Guided reflection resources, worksheets, and journaling prompts to deepen your self-awareness.",
        icon: Archive,
        color: "from-amber-500/20 to-amber-600/10",
        accent: "#D4A574",
        url: "https://drive.google.com/drive/folders/1nFc1Q9TuW3uubk7EnWl_DPBUy-NVUMQ4?usp=drive_link"
      },
      {
        id: "tools",
        title: "ACTION",
        description: "Workbooks, worksheets and more - growing monthly.",
        icon: Zap,
        color: "from-emerald-500/20 to-emerald-600/10",
        accent: "#5D9E9E",
        url: "https://drive.google.com/drive/folders/19nO7K6cTXjQU8EyP6VqiBDlAofIbNb2g?usp=drive_link"
      }
    ];
    
    // Transformation Quests - Annual tier only
    const TRANSFORMATION_QUESTS = [
      {
        id: "quest_wakeup",
        slug: "wake-up",
        title: "Wake Up",
        description: "Begin seeing yourself clearly. Awareness, self-observation, and pattern recognition."
      },
      {
        id: "quest_burnmap",
        slug: "burn-the-map",
        title: "Burn the Map",
        description: "Dismantling inherited scripts and rebuilding direction. Release what was never truly yours."
      },
      {
        id: "quest_whole",
        slug: "whole",
        title: "Whole",
        description: "Integration, boundaries, and aligned living. Stand in your gift and live with integrity."
      },
      {
        id: "quest_purposeful",
        slug: "purposeful-paths",
        title: "Purposeful Paths",
        description: "Direction and momentum. Build execution systems for sustainable transformation."
      }
    ];
    
    // Check subscription tier for quest access
    const isAnnual = hasAccess(subscription?.type, 'annual', user);
    const isPaid = hasAccess(subscription?.type, 'monthly', user);
    // Free users (no subscription) should not see the Transformation Quests section at all

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-[#a69373]/20 flex items-center justify-center mx-auto mb-4">
            <Archive className="w-8 h-8 text-[#a69373]" />
          </div>
          <h2 className="text-xl font-heading text-[#a69373] mb-2">The Vault</h2>
          <p className="text-white/60 text-sm max-w-md mx-auto">
            Downloadable resources to support your journey. Tap a folder to open in Google Drive.
          </p>
        </div>

        {/* Vault Sections - REFLECTION and ACTION */}
        <div className="space-y-3">
          {VAULT_SECTIONS.map((section) => (
            <Card 
              key={section.id}
              className="glass-card overflow-hidden cursor-pointer group hover:shadow-lg transition-all"
              onClick={() => window.open(section.url, '_blank', 'noopener,noreferrer')}
              data-testid={`vault-${section.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div 
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center shrink-0`}
                  >
                    <section.icon className="w-7 h-7" style={{ color: section.accent }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-white mb-1">{section.title}</h3>
                    <p className="text-white/60 text-sm line-clamp-2">{section.description}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-white/30 group-hover:text-[#a69373] transition-colors shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Transformation Quests Section - Below TOOLS, only for paid users */}
        {isPaid && (
          <div className="mt-6" id="transformation-quests-section">
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-5 h-5 text-[#a69373]" />
                <h3 className="text-sm font-heading text-[#a69373] uppercase tracking-wider">Transformation Quests</h3>
              </div>
              <p className="text-white/50 text-xs">
                30-day Field Kit journeys for Annual members. Each quest includes curated resources, reflection prompts, and your personal Field Briefing.
              </p>
            </div>
            
            {/* Quest cards - navigate to Field Kit pages */}
            <div className="space-y-2">
              {TRANSFORMATION_QUESTS.map((quest) => (
                <Card 
                  key={quest.id}
                  className="glass-card overflow-hidden cursor-pointer group hover:shadow-lg transition-all"
                  onClick={() => navigate(`/quest/${quest.slug}`)}
                  data-testid={`quest-${quest.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#a69373]/20 flex items-center justify-center shrink-0">
                        <Flame className="w-5 h-5 text-[#a69373]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white text-sm mb-0.5">{quest.title}</h4>
                        <p className="text-white/50 text-xs line-clamp-2">{quest.description}</p>
                      </div>
                      {isAnnual ? (
                        <Button
                          size="sm"
                          className="shrink-0 text-xs"
                          style={{ backgroundColor: '#a69373', color: '#ffffff' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quest/${quest.slug}`);
                          }}
                        >
                          Open Field Kit
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0 text-xs border-[#a69373]/50 text-[#a69373]"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open('https://www.truenorth-zone.com/pages/compass-membership', '_blank');
                          }}
                        >
                          Upgrade to Annual
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Info note */}
        <p className="text-center text-white/40 text-xs mt-6">
          Resources open in Google Drive for download
        </p>
      </div>
    );
  };

  // Accordion state for wellness tab
  const [wellnessExpandedTool, setWellnessExpandedTool] = useState(null);

  const handleWellnessToggle = (toolId) => {
    setWellnessExpandedTool(wellnessExpandedTool === toolId ? null : toolId);
  };

  const renderWellness = () => (
    <div className="space-y-6">
      {/* Meditation Section - Individual items gated based on free/paid tier */}
      <div className="relative">
        <MeditationSection />
        {/* No section-level lock - individual items handle their own gating */}
      </div>
      
      {/* Soundscapes Section - Individual items gated based on free/paid tier */}
      <div className="border-t border-white/10 pt-6 relative">
        <SoundscapesSection />
        {/* No section-level lock - individual items handle their own gating */}
      </div>
    </div>
  );

  const renderContent = () => {
    // Always render content structure - don't block on loading state
    // Individual components handle their own loading states
    switch (activeCategory) {
      case "tools":
        return renderTools();
      case "wellness":
        return renderWellness();
      default:
        return renderForYou();
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="discover-page">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 backdrop-blur-lg border-b border-black/5"
        style={{
          backgroundImage: `linear-gradient(rgba(11,40,33,0.85), rgba(11,40,33,0.9)), url('https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-lg font-heading text-[#a69373]">Discover</h1>
              <p className="text-xs text-[#a69373]/60 font-semibold">Your guide to transformation</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Bell className="w-4 h-4 text-[#a69373]" />
            </button>
            <a 
              href="https://www.truenorth-zone.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-sm text-[#a69373] text-sm font-semibold hover:bg-black/40 transition-colors"
              style={{ zIndex: 9999, position: 'relative', pointerEvents: 'all' }}
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </a>
          </div>
        </div>
      </header>

      <div className="px-4 pt-4">
        {/* Category Pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all
                  ${isActive 
                    ? "bg-[#7b9e7b] text-white shadow-md" 
                    : "bg-white text-[#a69373] hover:bg-gray-50"
                  }
                `}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {renderContent()}
      </div>

      <BottomNavigation />
    </div>
  );
}
